// CB:BEGIN admin-create-quote-page
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useApi } from '../../hooks/useApi';
import ResponsiveContainer from '../../components/layout/ResponsiveContainer';
import SettingsCard from '../../components/ui/SettingsCard';

const LINE_ITEM_CATEGORIES = [
  'TEAR_OFF',
  'MATERIALS',
  'LABOR',
  'DISPOSAL',
  'PERMITS',
  'WARRANTY',
  'OTHER',
] as const;

interface LineItemRow {
  id: string;
  category: (typeof LINE_ITEM_CATEGORIES)[number];
  description: string;
  quantity: number | '';
  unit: string;
  unitPriceCents: number | '';
  totalCents: number | '';
}

function toDateStr(d: Date): string {
  return d.toISOString().slice(0, 10);
}

const defaultLineItem = (): LineItemRow => ({
  id: crypto.randomUUID(),
  category: 'LABOR',
  description: '',
  quantity: '',
  unit: '',
  unitPriceCents: '',
  totalCents: '',
});

export interface QuoteFormValues {
  homeownerName: string;
  propertyAddress1: string;
  propertyCity: string;
  propertyState: string;
  propertyZip: string;
  contractorBusinessName: string;
  issueDate: string;
  expirationDate: string;
  roofType: string;
  roofSizeSqFt: number | '';
  roofSizeSquares: number | '';
  scopeOfWork: string;
  lineItems: LineItemRow[];
}

export interface ValidateQuoteFormResult {
  isValid: boolean;
  fieldErrors: Record<string, string>;
  summaryErrors: string[];
  firstErrorField: string | null;
}

function validateQuoteForm(values: QuoteFormValues): ValidateQuoteFormResult {
  const fieldErrors: Record<string, string> = {};
  const summaryErrors: string[] = [];
  const order: string[] = ['issueDate', 'expirationDate', 'homeownerName', 'propertyAddress1', 'propertyCity', 'propertyState', 'propertyZip', 'contractorBusinessName', 'roofType', 'roofSize', 'scopeOfWork', 'lineItems'];

  if (!values.issueDate?.trim()) {
    fieldErrors.issueDate = 'Issue date is required';
    summaryErrors.push('Quote issue date');
  }
  if (!values.expirationDate?.trim()) {
    fieldErrors.expirationDate = 'Expiration date is required';
    summaryErrors.push('Quote expiration date');
  }
  if (!values.homeownerName?.trim()) {
    fieldErrors.homeownerName = 'Homeowner name is required';
    summaryErrors.push('Homeowner name');
  }
  if (!values.propertyAddress1?.trim()) {
    fieldErrors.propertyAddress1 = 'Property address line 1 is required';
    summaryErrors.push('Property address');
  }
  if (!values.propertyCity?.trim()) {
    fieldErrors.propertyCity = 'City is required';
    summaryErrors.push('Property city');
  }
  if (!values.propertyState?.trim()) {
    fieldErrors.propertyState = 'State is required';
    summaryErrors.push('Property state');
  }
  if (!values.propertyZip?.trim()) {
    fieldErrors.propertyZip = 'ZIP code is required';
    summaryErrors.push('Property ZIP code');
  }
  if (!values.contractorBusinessName?.trim()) {
    fieldErrors.contractorBusinessName = 'Contractor name is required';
    summaryErrors.push('Contractor name');
  }
  if (!values.roofType?.trim()) {
    fieldErrors.roofType = 'Roof type is required';
    summaryErrors.push('Roof type');
  }
  const hasRoofSize = (values.roofSizeSqFt !== '' && Number(values.roofSizeSqFt) >= 0) || (values.roofSizeSquares !== '' && Number(values.roofSizeSquares) >= 0);
  if (!hasRoofSize) {
    fieldErrors.roofSize = 'Roof size (sq ft or squares) is required';
    summaryErrors.push('Roof size (sq ft or squares)');
  }
  if (!values.scopeOfWork?.trim()) {
    fieldErrors.scopeOfWork = 'Scope of work is required';
    summaryErrors.push('Scope of work narrative');
  }
  const everyHasDescription = values.lineItems.every((r) => r.description?.trim());
  const atLeastOnePriced = values.lineItems.some((r) => r.totalCents !== '' && Number(r.totalCents) > 0);
  if (!everyHasDescription) {
    fieldErrors.lineItems = 'Each line item needs a description. At least one line item must have a price (Total $ > 0); use – or leave empty for no price.';
    summaryErrors.push('Line items: description for each; at least one with a price');
  } else if (!atLeastOnePriced) {
    fieldErrors.lineItems = 'At least one line item must have a price (Total $ > 0). Use – or leave Total empty for no price.';
    summaryErrors.push('At least one line item with a price (Total $ > 0)');
  }

  const firstErrorField = order.find((k) => fieldErrors[k]) ?? null;
  return {
    isValid: summaryErrors.length === 0,
    fieldErrors,
    summaryErrors,
    firstErrorField,
  };
}

interface FormFieldProps {
  label: string;
  error?: string;
  children: React.ReactNode;
  htmlFor?: string;
}

function FormField({ label, error, children, htmlFor }: FormFieldProps) {
  const hasError = Boolean(error);
  return (
    <div>
      <label className={`block text-sm font-medium mb-1 ${hasError ? 'text-red-700' : 'text-gray-700'}`} htmlFor={htmlFor}>
        {label}
      </label>
      {children}
      {hasError && <p className="mt-1 text-sm text-red-600" role="alert">{error}</p>}
    </div>
  );
}

interface AdminCreateQuotePageProps {
  onNavigate?: (screen: string) => void;
}

const AdminCreateQuotePage: React.FC<AdminCreateQuotePageProps> = ({ onNavigate }) => {
  const api = useApi();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<{ quoteId: string; quoteNumber: string; pdfUrl?: string } | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [summaryErrors, setSummaryErrors] = useState<string[]>([]);
  const [firstErrorField, setFirstErrorField] = useState<string | null>(null);
  const [editingQuoteId, setEditingQuoteId] = useState<string | null>(() => {
    const params = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
    return params.get('quoteId');
  });
  const [loadingQuote, setLoadingQuote] = useState(false);
  const [templates, setTemplates] = useState<Array<{ id: string; name: string; description?: string | null }>>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [templateLoadError, setTemplateLoadError] = useState<string | null>(null);
  const [templateRetryKey, setTemplateRetryKey] = useState(0);
  const [templateSaveSuccess, setTemplateSaveSuccess] = useState(false);
  const [drafts, setDrafts] = useState<Array<{ id: string; quoteNumber: string; homeownerName: string | null; propertyCity: string; propertyState: string; updatedAt: string }>>([]);
  const [selectedDraftId, setSelectedDraftId] = useState<string | null>(null);
  const [loadingDrafts, setLoadingDrafts] = useState(false);
  const [draftLoadError, setDraftLoadError] = useState<string | null>(null);
  const [draftRetryKey, setDraftRetryKey] = useState(0);
  const [draftDeleteSuccess, setDraftDeleteSuccess] = useState(false);
  const [roofReportModalOpen, setRoofReportModalOpen] = useState(false);
  const [roofReportId, setRoofReportId] = useState<string | null>(null);
  const [measurementsSourceReportNumber, setMeasurementsSourceReportNumber] = useState<string | null>(null);
  const [eagleViewRidgesFt, setEagleViewRidgesFt] = useState<number | ''>('');
  const [eagleViewValleysFt, setEagleViewValleysFt] = useState<number | ''>('');
  const [eagleViewRakesFt, setEagleViewRakesFt] = useState<number | ''>('');
  const [eagleViewEavesFt, setEagleViewEavesFt] = useState<number | ''>('');
  const [uploadingRoofReport, setUploadingRoofReport] = useState(false);
  const [parsingRoofReport, setParsingRoofReport] = useState(false);
  const [roofReportExtracted, setRoofReportExtracted] = useState<Record<string, unknown> | null>(null);
  const [roofReportIdForApply, setRoofReportIdForApply] = useState<string | null>(null);

  const refs = useRef<Record<string, HTMLInputElement | HTMLTextAreaElement | HTMLDivElement | null>>({});
  const openedPdfUrlRef = useRef<string | null>(null);

  const [homeownerName, setHomeownerName] = useState('');
  const [homeownerEmail, setHomeownerEmail] = useState('');
  const [homeownerPhone, setHomeownerPhone] = useState('');
  const [propertyAddress1, setPropertyAddress1] = useState('');
  const [propertyAddress2, setPropertyAddress2] = useState('');
  const [propertyCity, setPropertyCity] = useState('');
  const [propertyState, setPropertyState] = useState('');
  const [propertyZip, setPropertyZip] = useState('');
  const [contractorBusinessName, setContractorBusinessName] = useState('');
  const [contractorLicense, setContractorLicense] = useState('');
  const [contractorInsurance, setContractorInsurance] = useState('');
  const [issueDate, setIssueDate] = useState(toDateStr(new Date()));
  const [expirationDate, setExpirationDate] = useState(toDateStr(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)));
  const [createdFromProjectId, setCreatedFromProjectId] = useState('');
  const [roofType, setRoofType] = useState('');
  const [roofSizeSqFt, setRoofSizeSqFt] = useState<number | ''>('');
  const [roofSizeSquares, setRoofSizeSquares] = useState<number | ''>('');
  const [roofPitch, setRoofPitch] = useState('');
  const [stories, setStories] = useState<number | ''>('');
  const [wasteFactorPct, setWasteFactorPct] = useState<number | ''>('');
  const [estimatedDurationDays, setEstimatedDurationDays] = useState<number | ''>('');
  const [scopeOfWork, setScopeOfWork] = useState('');
  const [exclusions, setExclusions] = useState('');
  const [materialsSummary, setMaterialsSummary] = useState('');
  const [ventilationSummary, setVentilationSummary] = useState('');
  const [paymentSchedule, setPaymentSchedule] = useState('');
  const [manufacturerWarranty, setManufacturerWarranty] = useState('');
  const [workmanshipWarranty, setWorkmanshipWarranty] = useState('');
  const [termsAndConditions, setTermsAndConditions] = useState('');
  const [systemShingles, setSystemShingles] = useState('');
  const [systemUnderlayment, setSystemUnderlayment] = useState('');
  const [systemStarter, setSystemStarter] = useState('');
  const [systemRidgeCap, setSystemRidgeCap] = useState('');
  const [systemLeakBarrier, setSystemLeakBarrier] = useState('');
  const [systemDripEdge, setSystemDripEdge] = useState('');
  const [systemVentilation, setSystemVentilation] = useState('');
  const [systemPipeBoots, setSystemPipeBoots] = useState('');
  const [systemDeckingAllowance, setSystemDeckingAllowance] = useState('');
  const [systemWarranties, setSystemWarranties] = useState('');
  const [allowanceExtraLayersCents, setAllowanceExtraLayersCents] = useState<number | ''>('');
  const [allowanceDeckingCents, setAllowanceDeckingCents] = useState<number | ''>('');
  const [allowanceRidgeVentCents, setAllowanceRidgeVentCents] = useState<number | ''>('');
  const [allowanceChimneyCricketCents, setAllowanceChimneyCricketCents] = useState<number | ''>('');

  const [lineItems, setLineItems] = useState<LineItemRow[]>([defaultLineItem(), defaultLineItem()]);

  const addLineItem = useCallback(() => {
    setLineItems((prev) => [...prev, defaultLineItem()]);
  }, []);

  const removeLineItem = useCallback((id: string) => {
    setLineItems((prev) => (prev.length > 1 ? prev.filter((r) => r.id !== id) : prev));
  }, []);

  const updateLineItem = useCallback((id: string, field: keyof LineItemRow, value: string | number) => {
    setLineItems((prev) =>
      prev.map((r) => (r.id === id ? { ...r, [field]: value } : r))
    );
  }, []);

  const subtotalCents = lineItems.reduce((sum, row) => {
    const t = row.totalCents === '' ? 0 : Number(row.totalCents);
    return sum + t;
  }, 0);
  const taxAndFeesCents = Math.round(subtotalCents * 0.1);
  const totalCents = subtotalCents + taxAndFeesCents;

  useEffect(() => {
    if (!firstErrorField) return;
    const el = refs.current[firstErrorField];
    if (el && 'focus' in el && typeof (el as HTMLInputElement).focus === 'function') {
      (el as HTMLInputElement).focus({ preventScroll: true });
    }
    el?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
  }, [firstErrorField]);

  // Auto-open PDF in new tab via signed-download endpoint when we have a PDF (create or generate-pdf)
  useEffect(() => {
    const quoteId = success?.quoteId;
    const hasPdf = Boolean(success?.pdfUrl);
    if (quoteId && hasPdf && quoteId !== openedPdfUrlRef.current) {
      openedPdfUrlRef.current = quoteId;
      api.get<{ url: string }>(`/api/admin/quotes/${quoteId}/pdf`).then(
        (data) => {
          if (data?.url) window.open(data.url, '_blank', 'noopener,noreferrer');
        },
        () => {}
      );
    }
    if (!hasPdf) openedPdfUrlRef.current = null;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- api identity unstable; only run when success changes
  }, [success?.quoteId, success?.pdfUrl]);

  // CB:BEGIN load-draft-quote (set ref only after success so Strict Mode cleanup doesn't prevent form update)
  const lastLoadedQuoteIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!editingQuoteId) {
      lastLoadedQuoteIdRef.current = null;
      return;
    }
    let cancelled = false;
    setLoadingQuote(true);
    setError(null);
    api
      .get<{
        id: string;
        quoteNumber: string;
        homeownerName: string | null;
        homeownerEmail: string | null;
        homeownerPhone: string | null;
        propertyAddress1: string;
        propertyAddress2: string | null;
        propertyCity: string;
        propertyState: string;
        propertyZip: string;
        contractorBusinessName: string;
        contractorLicense: string | null;
        contractorInsurance: string | null;
        issueDate: string;
        expirationDate: string;
        roofType: string;
        roofSizeSqFt: number | null;
        roofSizeSquares: number | null;
        roofPitch: string | null;
        stories: number | null;
        wasteFactorBps: number | null;
        estimatedDurationDays: number | null;
        scopeOfWork: string;
        exclusions: string | null;
        materialsSummary: string | null;
        ventilationSummary: string | null;
        paymentSchedule: string | null;
        manufacturerWarranty: string | null;
        workmanshipWarranty: string | null;
        termsAndConditions: string | null;
        systemComponents: Record<string, unknown> | null;
        allowancesAndRates: Record<string, unknown> | null;
        createdFromProjectId: string | null;
        roofReportId: string | null;
        lineItems: Array<{ category: string; description: string; quantity: number | null; unit: string | null; unitPriceCents: number | null; totalCents: number }>;
      }>(`/api/admin/quotes/${editingQuoteId}`)
      .then(async (quote) => {
        if (cancelled) return;
        setHomeownerName(quote.homeownerName ?? '');
        setRoofReportId(quote.roofReportId ?? null);
        if (quote.roofReportId) {
          api.get<{ reportNumber: string }>(`/api/admin/roof-reports/${quote.roofReportId}`).then(
            (r) => { if (!cancelled) setMeasurementsSourceReportNumber(r.reportNumber ?? null); },
            () => {}
          );
        } else {
          setMeasurementsSourceReportNumber(null);
        }
        setHomeownerEmail(quote.homeownerEmail ?? '');
        setHomeownerPhone(quote.homeownerPhone ?? '');
        setPropertyAddress1(quote.propertyAddress1);
        setPropertyAddress2(quote.propertyAddress2 ?? '');
        setPropertyCity(quote.propertyCity);
        setPropertyState(quote.propertyState);
        setPropertyZip(quote.propertyZip);
        setContractorBusinessName(quote.contractorBusinessName);
        setContractorLicense(quote.contractorLicense ?? '');
        setContractorInsurance(quote.contractorInsurance ?? '');
        const issueStr = typeof quote.issueDate === 'string' ? quote.issueDate.slice(0, 10) : (quote.issueDate as Date).toISOString().slice(0, 10);
        const expStr = typeof quote.expirationDate === 'string' ? quote.expirationDate.slice(0, 10) : (quote.expirationDate as Date).toISOString().slice(0, 10);
        setIssueDate(issueStr);
        setExpirationDate(expStr);
        setRoofType(quote.roofType);
        setRoofSizeSqFt(quote.roofSizeSqFt ?? '');
        setRoofSizeSquares(quote.roofSizeSquares ?? '');
        setRoofPitch(quote.roofPitch ?? '');
        setStories(quote.stories ?? '');
        setWasteFactorPct(quote.wasteFactorBps != null ? quote.wasteFactorBps / 100 : '');
        setEstimatedDurationDays(quote.estimatedDurationDays ?? '');
        setScopeOfWork(quote.scopeOfWork);
        setExclusions(quote.exclusions ?? '');
        setMaterialsSummary(quote.materialsSummary ?? '');
        setVentilationSummary(quote.ventilationSummary ?? '');
        setPaymentSchedule(quote.paymentSchedule ?? '');
        setManufacturerWarranty(quote.manufacturerWarranty ?? '');
        setWorkmanshipWarranty(quote.workmanshipWarranty ?? '');
        setTermsAndConditions(quote.termsAndConditions ?? '');
        setCreatedFromProjectId(quote.createdFromProjectId ?? '');
        const sc = (quote.systemComponents || {}) as Record<string, string>;
        setSystemShingles(sc.shingles ?? '');
        setSystemUnderlayment(sc.underlayment ?? '');
        setSystemStarter(sc.starter ?? '');
        setSystemRidgeCap(sc.ridgeCap ?? '');
        setSystemLeakBarrier(sc.leakBarrier ?? '');
        setSystemDripEdge(sc.dripEdge ?? '');
        setSystemVentilation(sc.ventilation ?? '');
        setSystemPipeBoots(sc.pipeBoots ?? '');
        setSystemDeckingAllowance(sc.deckingAllowance ?? '');
        setSystemWarranties(sc.warranties ?? '');
        const ar = (quote.allowancesAndRates || {}) as Record<string, number>;
        setAllowanceExtraLayersCents(ar.extraLayersPer100SqFtCents ?? '');
        setAllowanceDeckingCents(ar.deckingPerSheetCents ?? '');
        setAllowanceRidgeVentCents(ar.ridgeVentPerLfCents ?? '');
        setAllowanceChimneyCricketCents(ar.chimneyCricketPerChimneyCents ?? '');
        setLineItems(
          quote.lineItems.map((i) => ({
            id: crypto.randomUUID(),
            category: i.category as LineItemRow['category'],
            description: i.description,
            quantity: i.quantity ?? '',
            unit: i.unit ?? '',
            unitPriceCents: i.unitPriceCents ?? '',
            totalCents: i.totalCents === 0 ? '' : i.totalCents,
          }))
        );
        if (!cancelled) lastLoadedQuoteIdRef.current = editingQuoteId;
      })
      .catch(() => {
        if (!cancelled) setError('Failed to load quote');
      })
      .finally(() => {
        if (!cancelled) setLoadingQuote(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- api identity unstable; only run when editingQuoteId changes
  }, [editingQuoteId]);
  // CB:END load-draft-quote

  // CB:BEGIN draft-list-fetch (on mount and on Retry, like templates)
  useEffect(() => {
    let cancelled = false;
    setDraftLoadError(null);
    setLoadingDrafts(true);
    const params = new URLSearchParams({ status: 'DRAFT' });
    api
      .get<Array<{ id: string; quoteNumber: string; homeownerName: string | null; propertyCity: string; propertyState: string; updatedAt: string }>>(
        `/api/admin/quotes?${params.toString()}`
      )
      .then((list) => {
        if (!cancelled) {
          setDrafts(list || []);
          if (editingQuoteId && (list || []).some((d) => d.id === editingQuoteId)) {
            setSelectedDraftId(editingQuoteId);
          }
        }
      })
      .catch(() => {
        if (!cancelled) {
          setDrafts([]);
          setDraftLoadError('Failed to load drafts. Check API connection.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingDrafts(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run on mount and when draftRetryKey changes
  }, [draftRetryKey]);
  // CB:END draft-list-fetch

  // CB:BEGIN template-fetch (run on mount and on Retry; timeout so "Loading templates…" does not hang)
  const TEMPLATE_FETCH_TIMEOUT_MS = 10000;
  useEffect(() => {
    let cancelled = false;
    setTemplateLoadError(null);
    setLoadingTemplates(true);
    const fetchPromise = api.get<Array<{ id: string; name: string; description?: string | null }>>('/api/admin/quote-templates');
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error('Request timed out')), TEMPLATE_FETCH_TIMEOUT_MS);
    });
    Promise.race([fetchPromise, timeoutPromise])
      .then((list) => {
        if (!cancelled) setTemplates(list || []);
      })
      .catch((err) => {
        if (!cancelled) {
          setTemplates([]);
          setTemplateLoadError(err?.message === 'Request timed out' ? 'Templates took too long. Check API connection.' : 'Failed to load templates. Check API connection.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingTemplates(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run on mount and when templateRetryKey changes (Retry)
  }, [templateRetryKey]);
  // CB:END template-fetch

  const applyTemplate = useCallback(
    async (templateId: string) => {
      try {
        const t = await api.get<{
          id: string;
          name: string;
          data: {
            systemComponents?: Record<string, string | number | null>;
            scopeOfWork?: string;
            termsAndConditions?: string;
            exclusions?: string;
            allowancesAndRates?: Record<string, number | null>;
            lineItems?: Array<{ category: string; description: string; quantity?: number | null; unit?: string | null; unitPriceCents?: number | null; totalCents: number }>;
            roofType?: string;
            wasteFactorBps?: number;
            estimatedDurationDays?: number;
          };
        }>(`/api/admin/quote-templates/${templateId}`);
        const data = (t as any).data;
        if (!data || typeof data !== 'object') return;
        if (data.roofType != null) setRoofType(String(data.roofType));
        if (data.wasteFactorBps != null) setWasteFactorPct(data.wasteFactorBps / 100);
        if (data.estimatedDurationDays != null) setEstimatedDurationDays(data.estimatedDurationDays);
        if (data.scopeOfWork != null) setScopeOfWork(String(data.scopeOfWork));
        if (data.exclusions != null) setExclusions(String(data.exclusions));
        if (data.termsAndConditions != null) setTermsAndConditions(String(data.termsAndConditions));
        const sc = data.systemComponents as Record<string, string> | undefined;
        if (sc) {
          setSystemShingles(sc.shingles ?? '');
          setSystemUnderlayment(sc.underlayment ?? '');
          setSystemStarter(sc.starter ?? '');
          setSystemRidgeCap(sc.ridgeCap ?? '');
          setSystemLeakBarrier(sc.leakBarrier ?? '');
          setSystemDripEdge(sc.dripEdge ?? '');
          setSystemVentilation(sc.ventilation ?? '');
          setSystemPipeBoots(sc.pipeBoots ?? '');
          setSystemDeckingAllowance(sc.deckingAllowance ?? '');
          setSystemWarranties(sc.warranties ?? '');
        }
        const ar = data.allowancesAndRates as Record<string, number> | undefined;
        if (ar) {
          setAllowanceExtraLayersCents(ar.extraLayersPer100SqFtCents ?? '');
          setAllowanceDeckingCents(ar.deckingPerSheetCents ?? '');
          setAllowanceRidgeVentCents(ar.ridgeVentPerLfCents ?? '');
          setAllowanceChimneyCricketCents(ar.chimneyCricketPerChimneyCents ?? '');
        }
        if (Array.isArray(data.lineItems) && data.lineItems.length > 0) {
          setLineItems(
            data.lineItems.map((i) => ({
              id: crypto.randomUUID(),
              category: (i.category as LineItemRow['category']) ?? 'LABOR',
              description: i.description ?? '',
              quantity: i.quantity ?? '',
              unit: i.unit ?? '',
              unitPriceCents: i.unitPriceCents ?? '',
              totalCents: i.totalCents === 0 ? '' : i.totalCents,
            }))
          );
        }
        setSelectedTemplateId(templateId);
      } catch {
        setError('Failed to load template');
      }
    },
    [api]
  );

  const saveAsTemplate = useCallback(async () => {
    const name = window.prompt('Template name');
    if (!name?.trim()) return;
    try {
      const payload = {
        name: name.trim(),
        description: 'Saved from Create Quote form',
        data: {
          roofType: roofType || undefined,
          wasteFactorBps: wasteFactorPct === '' ? undefined : Math.round(Number(wasteFactorPct) * 100),
          estimatedDurationDays: estimatedDurationDays === '' ? undefined : Number(estimatedDurationDays),
          scopeOfWork: scopeOfWork || undefined,
          exclusions: exclusions || undefined,
          termsAndConditions: termsAndConditions || undefined,
          systemComponents: [systemShingles, systemUnderlayment, systemStarter, systemRidgeCap, systemLeakBarrier, systemDripEdge, systemVentilation, systemPipeBoots, systemDeckingAllowance, systemWarranties].some((s) => s.trim() !== '')
            ? {
                shingles: systemShingles.trim() || undefined,
                underlayment: systemUnderlayment.trim() || undefined,
                starter: systemStarter.trim() || undefined,
                ridgeCap: systemRidgeCap.trim() || undefined,
                leakBarrier: systemLeakBarrier.trim() || undefined,
                dripEdge: systemDripEdge.trim() || undefined,
                ventilation: systemVentilation.trim() || undefined,
                pipeBoots: systemPipeBoots.trim() || undefined,
                deckingAllowance: systemDeckingAllowance.trim() || undefined,
                warranties: systemWarranties.trim() || undefined,
              }
            : undefined,
          allowancesAndRates:
            allowanceExtraLayersCents !== '' || allowanceDeckingCents !== '' || allowanceRidgeVentCents !== '' || allowanceChimneyCricketCents !== ''
              ? {
                  extraLayersPer100SqFtCents: allowanceExtraLayersCents !== '' ? Number(allowanceExtraLayersCents) : undefined,
                  deckingPerSheetCents: allowanceDeckingCents !== '' ? Number(allowanceDeckingCents) : undefined,
                  ridgeVentPerLfCents: allowanceRidgeVentCents !== '' ? Number(allowanceRidgeVentCents) : undefined,
                  chimneyCricketPerChimneyCents: allowanceChimneyCricketCents !== '' ? Number(allowanceChimneyCricketCents) : undefined,
                }
              : undefined,
          lineItems: lineItems.map((r) => ({
            category: r.category,
            description: r.description.trim() || 'Item',
            quantity: r.quantity === '' ? undefined : Number(r.quantity),
            unit: r.unit || undefined,
            unitPriceCents: r.unitPriceCents === '' ? undefined : Number(r.unitPriceCents),
            totalCents: r.totalCents === '' ? 0 : Number(r.totalCents),
          })),
        },
      };
      await api.post('/api/admin/quote-templates', payload);
      const list = await api.get<Array<{ id: string; name: string }>>('/api/admin/quote-templates');
      setTemplates(list || []);
      setSelectedTemplateId(null);
      setTemplateSaveSuccess(true);
      setError(null);
      setTimeout(() => setTemplateSaveSuccess(false), 3000);
    } catch (err: any) {
      setError(err?.message || 'Failed to save template');
    }
  }, [
    api,
    roofType,
    wasteFactorPct,
    estimatedDurationDays,
    scopeOfWork,
    exclusions,
    termsAndConditions,
    systemShingles,
    systemUnderlayment,
    systemStarter,
    systemRidgeCap,
    systemLeakBarrier,
    systemDripEdge,
    systemVentilation,
    systemPipeBoots,
    systemDeckingAllowance,
    systemWarranties,
    allowanceExtraLayersCents,
    allowanceDeckingCents,
    allowanceRidgeVentCents,
    allowanceChimneyCricketCents,
    lineItems,
  ]);

  const updateTemplate = useCallback(
    async (templateId: string) => {
      try {
        const payload = {
          data: {
            roofType: roofType || undefined,
            wasteFactorBps: wasteFactorPct === '' ? undefined : Math.round(Number(wasteFactorPct) * 100),
            estimatedDurationDays: estimatedDurationDays === '' ? undefined : Number(estimatedDurationDays),
            scopeOfWork: scopeOfWork || undefined,
            exclusions: exclusions || undefined,
            termsAndConditions: termsAndConditions || undefined,
            systemComponents: [systemShingles, systemUnderlayment, systemStarter, systemRidgeCap, systemLeakBarrier, systemDripEdge, systemVentilation, systemPipeBoots, systemDeckingAllowance, systemWarranties].some((s) => s.trim() !== '')
              ? {
                  shingles: systemShingles.trim() || undefined,
                  underlayment: systemUnderlayment.trim() || undefined,
                  starter: systemStarter.trim() || undefined,
                  ridgeCap: systemRidgeCap.trim() || undefined,
                  leakBarrier: systemLeakBarrier.trim() || undefined,
                  dripEdge: systemDripEdge.trim() || undefined,
                  ventilation: systemVentilation.trim() || undefined,
                  pipeBoots: systemPipeBoots.trim() || undefined,
                  deckingAllowance: systemDeckingAllowance.trim() || undefined,
                  warranties: systemWarranties.trim() || undefined,
                }
              : undefined,
            allowancesAndRates:
              allowanceExtraLayersCents !== '' || allowanceDeckingCents !== '' || allowanceRidgeVentCents !== '' || allowanceChimneyCricketCents !== ''
                ? {
                    extraLayersPer100SqFtCents: allowanceExtraLayersCents !== '' ? Number(allowanceExtraLayersCents) : undefined,
                    deckingPerSheetCents: allowanceDeckingCents !== '' ? Number(allowanceDeckingCents) : undefined,
                    ridgeVentPerLfCents: allowanceRidgeVentCents !== '' ? Number(allowanceRidgeVentCents) : undefined,
                    chimneyCricketPerChimneyCents: allowanceChimneyCricketCents !== '' ? Number(allowanceChimneyCricketCents) : undefined,
                  }
                : undefined,
            lineItems: lineItems.map((r) => ({
              category: r.category,
              description: r.description.trim() || 'Item',
              quantity: r.quantity === '' ? undefined : Number(r.quantity),
              unit: r.unit || undefined,
              unitPriceCents: r.unitPriceCents === '' ? undefined : Number(r.unitPriceCents),
              totalCents: r.totalCents === '' ? 0 : Number(r.totalCents),
            })),
          },
        };
        await api.put(`/api/admin/quote-templates/${templateId}`, payload);
      } catch (err: any) {
        setError(err?.message || 'Failed to update template');
      }
    },
    [
      api,
      roofType,
      wasteFactorPct,
      estimatedDurationDays,
      scopeOfWork,
      exclusions,
      termsAndConditions,
      systemShingles,
      systemUnderlayment,
      systemStarter,
      systemRidgeCap,
      systemLeakBarrier,
      systemDripEdge,
      systemVentilation,
      systemPipeBoots,
      systemDeckingAllowance,
      systemWarranties,
      allowanceExtraLayersCents,
      allowanceDeckingCents,
      allowanceRidgeVentCents,
      allowanceChimneyCricketCents,
      lineItems,
    ]
  );

  const deleteTemplate = useCallback(
    async (templateId: string) => {
      if (!window.confirm('Delete this template?')) return;
      try {
        await api.delete(`/api/admin/quote-templates/${templateId}`);
        setTemplates((prev) => prev.filter((t) => t.id !== templateId));
        if (selectedTemplateId === templateId) setSelectedTemplateId(null);
      } catch (err: any) {
        setError(err?.message || 'Failed to delete template');
      }
    },
    [api, selectedTemplateId]
  );

  const deleteDraft = useCallback(
    async (draftId: string) => {
      const draft = drafts.find((d) => d.id === draftId);
      const label = draft ? `${draft.quoteNumber} — ${draft.homeownerName ?? '—'}` : draftId;
      if (!window.confirm(`Delete draft ${label}? This cannot be undone.`)) return;
      try {
        await api.delete(`/api/admin/quotes/${draftId}`);
        setDrafts((prev) => prev.filter((d) => d.id !== draftId));
        if (selectedDraftId === draftId) setSelectedDraftId(null);
        if (editingQuoteId === draftId) {
          setEditingQuoteId(null);
          if (typeof window !== 'undefined') {
            const url = new URL(window.location.href);
            url.searchParams.delete('quoteId');
            window.history.replaceState({}, '', url.pathname + (url.search || ''));
          }
        }
        setDraftDeleteSuccess(true);
        setTimeout(() => setDraftDeleteSuccess(false), 3000);
      } catch (err: any) {
        setError(err?.message || 'Failed to delete draft');
      }
    },
    [api, drafts, selectedDraftId, editingQuoteId]
  );

  // CB:BEGIN roof-report-upload-parse-apply
  const roofReportFileRef = useRef<HTMLInputElement | null>(null);
  const handleRoofReportUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      setError(null);
      setUploadingRoofReport(true);
      setRoofReportExtracted(null);
      setRoofReportIdForApply(null);
      try {
        const formData = new FormData();
        formData.append('file', file);
        const res = await api.post<{ roofReportId: string; reportKey: string; reportNumber: string }>(
          '/api/admin/roof-reports/eagleview/upload',
          formData
        );
        setRoofReportIdForApply(res.roofReportId);
        setParsingRoofReport(true);
        const parseRes = await api.post<{ extracted: Record<string, unknown>; warnings: string[]; reportNumber: string }>(
          `/api/admin/roof-reports/${res.roofReportId}/parse`,
          {}
        );
        setRoofReportExtracted(parseRes.extracted || {});
        setMeasurementsSourceReportNumber(parseRes.reportNumber || res.reportNumber);
      } catch (err: any) {
        setError(err?.message || 'Roof report upload or parse failed');
      } finally {
        setUploadingRoofReport(false);
        setParsingRoofReport(false);
        e.target.value = '';
      }
    },
    [api]
  );

  const handleApplyRoofReportToQuote = useCallback(() => {
    const ext = roofReportExtracted;
    if (!ext) return;
    if (ext.roofAreaSqFt != null) setRoofSizeSqFt(Number(ext.roofAreaSqFt));
    if (ext.predominantPitch != null) setRoofPitch(String(ext.predominantPitch));
    if (ext.stories != null) setStories(Number(ext.stories));
    if (ext.ridgesFt != null) setEagleViewRidgesFt(Number(ext.ridgesFt));
    if (ext.valleysFt != null) setEagleViewValleysFt(Number(ext.valleysFt));
    if (ext.rakesFt != null) setEagleViewRakesFt(Number(ext.rakesFt));
    if (ext.eavesFt != null) setEagleViewEavesFt(Number(ext.eavesFt));
    if (roofReportIdForApply) setRoofReportId(roofReportIdForApply);
    if (wasteFactorPct === '' && ext.predominantPitch === '7/12') setWasteFactorPct(10);
    setRoofReportModalOpen(false);
    setRoofReportExtracted(null);
    setRoofReportIdForApply(null);
  }, [roofReportExtracted, roofReportIdForApply, measurementsSourceReportNumber, wasteFactorPct]);

  // CB:END roof-report-upload-parse-apply

  const buildPayload = useCallback(() => {
    return {
      homeownerName: homeownerName.trim() || undefined,
      homeownerEmail: homeownerEmail.trim() || undefined,
      homeownerPhone: homeownerPhone.trim() || undefined,
      propertyAddress1: propertyAddress1.trim(),
      propertyAddress2: propertyAddress2.trim() || undefined,
      propertyCity: propertyCity.trim(),
      propertyState: propertyState.trim(),
      propertyZip: propertyZip.trim(),
      contractorBusinessName: contractorBusinessName.trim(),
      contractorLicense: contractorLicense.trim() || undefined,
      contractorInsurance: contractorInsurance.trim() || undefined,
      issueDate,
      expirationDate,
      createdFromProjectId: createdFromProjectId.trim() || undefined,
      roofReportId: roofReportId || undefined,
      roofType: roofType.trim(),
      roofSizeSqFt: roofSizeSqFt === '' ? undefined : Number(roofSizeSqFt),
      roofSizeSquares: roofSizeSquares === '' ? undefined : Number(roofSizeSquares),
      roofPitch: roofPitch.trim() || undefined,
      stories: stories === '' ? undefined : Number(stories),
      wasteFactorBps: wasteFactorPct === '' ? undefined : Math.round(Number(wasteFactorPct) * 100),
      estimatedDurationDays: estimatedDurationDays === '' ? undefined : Number(estimatedDurationDays),
      scopeOfWork: scopeOfWork.trim(),
      exclusions: exclusions.trim() || undefined,
      materialsSummary: materialsSummary.trim() || undefined,
      ventilationSummary: ventilationSummary.trim() || undefined,
      paymentSchedule: paymentSchedule.trim() || undefined,
      manufacturerWarranty: manufacturerWarranty.trim() || undefined,
      workmanshipWarranty: workmanshipWarranty.trim() || undefined,
      termsAndConditions: termsAndConditions.trim() || undefined,
      systemComponents: [systemShingles, systemUnderlayment, systemStarter, systemRidgeCap, systemLeakBarrier, systemDripEdge, systemVentilation, systemPipeBoots, systemDeckingAllowance, systemWarranties].some((s) => s.trim() !== '')
        ? {
            ...(systemShingles.trim() && { shingles: systemShingles.trim() }),
            ...(systemUnderlayment.trim() && { underlayment: systemUnderlayment.trim() }),
            ...(systemStarter.trim() && { starter: systemStarter.trim() }),
            ...(systemRidgeCap.trim() && { ridgeCap: systemRidgeCap.trim() }),
            ...(systemLeakBarrier.trim() && { leakBarrier: systemLeakBarrier.trim() }),
            ...(systemDripEdge.trim() && { dripEdge: systemDripEdge.trim() }),
            ...(systemVentilation.trim() && { ventilation: systemVentilation.trim() }),
            ...(systemPipeBoots.trim() && { pipeBoots: systemPipeBoots.trim() }),
            ...(systemDeckingAllowance.trim() && { deckingAllowance: systemDeckingAllowance.trim() }),
            ...(systemWarranties.trim() && { warranties: systemWarranties.trim() }),
          }
        : undefined,
      allowancesAndRates:
        allowanceExtraLayersCents !== '' || allowanceDeckingCents !== '' || allowanceRidgeVentCents !== '' || allowanceChimneyCricketCents !== ''
          ? {
              ...(allowanceExtraLayersCents !== '' && { extraLayersPer100SqFtCents: Number(allowanceExtraLayersCents) }),
              ...(allowanceDeckingCents !== '' && { deckingPerSheetCents: Number(allowanceDeckingCents) }),
              ...(allowanceRidgeVentCents !== '' && { ridgeVentPerLfCents: Number(allowanceRidgeVentCents) }),
              ...(allowanceChimneyCricketCents !== '' && { chimneyCricketPerChimneyCents: Number(allowanceChimneyCricketCents) }),
            }
          : undefined,
      lineItems: lineItems.map((r) => ({
        category: r.category,
        description: r.description.trim(),
        quantity: r.quantity === '' ? undefined : Number(r.quantity),
        unit: r.unit.trim() || undefined,
        unitPriceCents: r.unitPriceCents === '' ? undefined : Number(r.unitPriceCents),
        totalCents: r.totalCents === '' ? 0 : Number(r.totalCents),
      })),
    };
  }, [
    homeownerName,
    homeownerEmail,
    homeownerPhone,
    propertyAddress1,
    propertyAddress2,
    propertyCity,
    propertyState,
    propertyZip,
    contractorBusinessName,
    contractorLicense,
    contractorInsurance,
    issueDate,
    expirationDate,
    createdFromProjectId,
    roofReportId,
    roofType,
    roofSizeSqFt,
    roofSizeSquares,
    roofPitch,
    stories,
    wasteFactorPct,
    estimatedDurationDays,
    scopeOfWork,
    exclusions,
    materialsSummary,
    ventilationSummary,
    paymentSchedule,
    manufacturerWarranty,
    workmanshipWarranty,
    termsAndConditions,
    systemShingles,
    systemUnderlayment,
    systemStarter,
    systemRidgeCap,
    systemLeakBarrier,
    systemDripEdge,
    systemVentilation,
    systemPipeBoots,
    systemDeckingAllowance,
    systemWarranties,
    allowanceExtraLayersCents,
    allowanceDeckingCents,
    allowanceRidgeVentCents,
    allowanceChimneyCricketCents,
    lineItems,
  ]);

  const handleSubmit = async (e: React.FormEvent, options?: { draft?: boolean; generatePdfAfterUpdate?: boolean }) => {
    e.preventDefault();
    if (submitting) return;
    const isDraft = options?.draft === true;
    if (!isDraft) {
      const values: QuoteFormValues = {
        homeownerName,
        propertyAddress1,
        propertyCity,
        propertyState,
        propertyZip,
        contractorBusinessName,
        issueDate,
        expirationDate,
        roofType,
        roofSizeSqFt,
        roofSizeSquares,
        scopeOfWork,
        lineItems,
      };
      const result = validateQuoteForm(values);
      if (!result.isValid) {
        setFieldErrors(result.fieldErrors);
        setSummaryErrors(result.summaryErrors);
        setFirstErrorField(result.firstErrorField);
        return;
      }
    }
    setFieldErrors({});
    setSummaryErrors([]);
    setFirstErrorField(null);
    setError(null);
    setSubmitting(true);
    try {
      const payload = buildPayload();
      if (editingQuoteId) {
        const updated = await api.put<{ id: string; quoteNumber: string }>(`/api/admin/quotes/${editingQuoteId}`, payload);
        if (options?.generatePdfAfterUpdate) {
          const gen = await api.post<{ pdfUrl: string }>(`/api/admin/quotes/${editingQuoteId}/generate-pdf`, {});
          setSuccess({ quoteId: editingQuoteId, quoteNumber: updated.quoteNumber, pdfUrl: gen.pdfUrl });
        } else {
          setSuccess({ quoteId: editingQuoteId, quoteNumber: updated.quoteNumber });
        }
      } else {
        const body = options?.draft ? { ...payload, draft: true } : payload;
        const res = await api.post<{ quoteId: string; quoteNumber: string; pdfUrl?: string }>(
          '/api/admin/quotes',
          body
        );
        let pdfUrl = res.pdfUrl;
        if (!options?.draft && res.quoteId && !pdfUrl) {
          try {
            const quote = await api.get<{ pdfUrl?: string }>(`/api/admin/quotes/${res.quoteId}`);
            pdfUrl = quote.pdfUrl ?? undefined;
          } catch {
            // ignore
          }
        }
        setSuccess({ quoteId: res.quoteId, quoteNumber: res.quoteNumber, pdfUrl });
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to save quote');
    } finally {
      setSubmitting(false);
    }
  };

  const handleGeneratePdf = async () => {
    if (!success?.quoteId || success.pdfUrl) return;
    setSubmitting(true);
    setError(null);
    try {
      const gen = await api.post<{ pdfUrl: string }>(`/api/admin/quotes/${success.quoteId}/generate-pdf`, {});
      setSuccess((prev) => (prev ? { ...prev, pdfUrl: gen.pdfUrl } : null));
    } catch (err: any) {
      setError(err?.message || 'Failed to generate PDF');
    } finally {
      setSubmitting(false);
    }
  };

  /** Fetch fresh signed PDF URL from backend and open in new tab. If PDF not found (404), generate it first then retry. */
  const openQuotePdf = useCallback(
    async (quoteId: string) => {
      try {
        let data: { url: string } | null = null;
        try {
          data = await api.get<{ url: string }>(`/api/admin/quotes/${quoteId}/pdf`);
        } catch {
          // PDF not found (e.g. draft never generated, or pdfKey not persisted) – generate then retry
          await api.post(`/api/admin/quotes/${quoteId}/generate-pdf`, {});
          data = await api.get<{ url: string }>(`/api/admin/quotes/${quoteId}/pdf`);
        }
        if (data?.url) window.open(data.url, '_blank', 'noopener,noreferrer');
        else setError('Could not open PDF');
      } catch (err: any) {
        setError(err?.message || 'Could not open PDF');
      }
    },
    [api]
  );

  const inputErrorClass = (key: string) =>
    fieldErrors[key] ? 'border-red-500 ring-1 ring-red-300 bg-red-50/50' : 'border-gray-300';
  /** Inline red highlight for inputs when validation fails (avoids Tailwind purge of dynamic classes). */
  const inputErrorStyle = (key: string): React.CSSProperties =>
    fieldErrors[key] ? { borderColor: '#ef4444', boxShadow: '0 0 0 1px #fca5a5', backgroundColor: '#fef2f2' } : {};

  if (success) {
    const isDraft = !success.pdfUrl;
    return (
      <ResponsiveContainer>
        <div style={{ padding: 24, maxWidth: 1400, margin: '0 auto' }}>
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 max-w-2xl">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            {isDraft ? 'Draft saved' : 'Quote created'}
          </h2>
          <p className="text-gray-600 mb-4">
            Quote <strong>{success.quoteNumber}</strong> {isDraft ? 'has been saved as a draft.' : 'has been created.'}
          </p>
          {success.pdfUrl ? (
            <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="font-medium text-green-800 mb-2">Your PDF is ready</p>
              <button
                type="button"
                onClick={() => openQuotePdf(success.quoteId)}
                className="inline-block px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg"
              >
                Download PDF
              </button>
              <p className="text-sm text-green-700 mt-2">Uses a secure signed URL (10 min). Opens in a new tab.</p>
            </div>
          ) : null}
          <div className="flex flex-wrap gap-3">
            {!success.pdfUrl ? (
              <>
                <button
                  type="button"
                  onClick={handleGeneratePdf}
                  disabled={submitting}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-medium rounded-lg"
                >
                  {submitting ? 'Generating…' : 'Generate PDF'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setSuccess(null);
                    window.location.assign(`${window.location.pathname}?quoteId=${success.quoteId}`);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Edit draft
                </button>
              </>
            ) : null}
            <button
              type="button"
              onClick={() => {
                setSuccess(null);
                setEditingQuoteId(null);
                setLineItems([defaultLineItem(), defaultLineItem()]);
                if (typeof window !== 'undefined') {
                  const url = new URL(window.location.href);
                  url.searchParams.delete('quoteId');
                  window.history.replaceState({}, '', url.pathname + url.search);
                }
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Create another quote
            </button>
            {onNavigate && (
              <button type="button" onClick={() => onNavigate('admin-dashboard')} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                Back to Dashboard
              </button>
            )}
          </div>
        </div>
        </div>
      </ResponsiveContainer>
    );
  }

  if (loadingQuote && editingQuoteId) {
    return (
      <ResponsiveContainer>
        <div className="min-h-full bg-slate-50/70 px-6 py-12 max-w-[1400px] mx-auto text-center text-slate-600">Loading quote…</div>
      </ResponsiveContainer>
    );
  }

  return (
    <ResponsiveContainer>
      <div className="min-h-full bg-slate-50/70 px-6 py-8 max-w-[1400px] mx-auto">
      <form onSubmit={(e) => handleSubmit(e)} className="space-y-6">
        {/* Top: Back left, title + subtitle, primary CTA right */}
        <div className="mb-8">
          <div className="flex flex-wrap items-center justify-between gap-6 mb-4">
            <div className="flex items-center flex-1 gap-4 min-w-0">
              {onNavigate && (
                <button
                  type="button"
                  onClick={() => onNavigate('admin-dashboard')}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-slate-700 text-sm font-medium hover:bg-slate-50 hover:border-slate-300 transition-colors shadow-sm"
                >
                  ← Back to Admin Dashboard
                </button>
              )}
              <div className="min-w-0">
                <h1 className="text-2xl md:text-3xl font-bold text-slate-800 m-0 tracking-tight">
                  {editingQuoteId ? 'Edit draft quote' : 'Create Quote'}
                </h1>
                <p className="text-slate-600 mt-2 text-base">
                  {editingQuoteId
                    ? 'Update the draft and optionally generate a new PDF.'
                    : 'Fill required sections and at least one line item. Amounts stored as cents.'}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                disabled={submitting}
                onClick={(e) => handleSubmit(e as unknown as React.FormEvent, { draft: true })}
                className="px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-slate-700 text-sm font-medium hover:bg-slate-50 hover:border-slate-300 transition-colors shadow-sm disabled:opacity-60"
              >
                Save Draft
              </button>
              {editingQuoteId ? (
                <>
                  <button
                    type="button"
                    disabled={submitting}
                    onClick={(e) => handleSubmit(e as unknown as React.FormEvent, { generatePdfAfterUpdate: false })}
                    className="px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-slate-700 text-sm font-semibold hover:bg-slate-50 hover:border-slate-300 transition-colors shadow-sm disabled:opacity-60"
                  >
                    {submitting ? 'Saving…' : 'Update draft'}
                  </button>
                  <button
                    type="button"
                    disabled={submitting}
                    onClick={(e) => handleSubmit(e as unknown as React.FormEvent, { generatePdfAfterUpdate: true })}
                    className="px-5 py-2.5 rounded-xl bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 active:bg-emerald-800 transition-colors shadow-sm disabled:opacity-70"
                  >
                    {submitting ? 'Saving…' : 'Update & generate PDF'}
                  </button>
                </>
              ) : (
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-5 py-2.5 rounded-xl bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 active:bg-emerald-800 transition-colors shadow-sm disabled:opacity-70"
                >
                  {submitting ? 'Creating…' : 'Create Quote'}
                </button>
              )}
            </div>
          </div>
        </div>
        {templateSaveSuccess && (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50/80 px-5 py-4 mb-6 shadow-sm" role="status">
            <strong className="text-emerald-800">Template saved.</strong>{' '}
            <span className="text-emerald-700">You can apply it from the Templates dropdown.</span>
          </div>
        )}
        {draftDeleteSuccess && (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50/80 px-5 py-4 mb-6 shadow-sm" role="status">
            <strong className="text-emerald-800">Draft deleted.</strong>
          </div>
        )}
        {error && (
          <div className="rounded-xl border border-red-200 bg-red-50/80 px-5 py-4 mb-6 shadow-sm" role="alert">
            <strong className="text-red-800">Error:</strong> <span className="text-red-700">{error}</span>
          </div>
        )}
        {summaryErrors.length > 0 && (
          <div className="rounded-xl border border-red-200 bg-red-50/80 px-5 py-4 mb-6 shadow-sm" role="alert">
            <h3 className="text-sm font-semibold text-red-800 m-0 mb-2">Missing required items</h3>
            <ul className="list-disc pl-5 m-0 text-sm text-red-700 space-y-1">
              {summaryErrors.map((msg, i) => (
                <li key={i}>{msg}</li>
              ))}
            </ul>
          </div>
        )}

        <SettingsCard title={<><span>Quote Information</span> <strong style={{ fontWeight: 700 }}>(**Required**)</strong></>}>
          <div className="mb-6 p-4 rounded-xl border border-slate-200/80 bg-slate-50/60">
            <label className="block text-sm font-semibold text-slate-700 mb-3">Drafts</label>
            <div className="flex flex-wrap items-center gap-3">
              <select
                value={selectedDraftId ?? ''}
                onChange={(e) => setSelectedDraftId(e.target.value || null)}
                className="border border-slate-200 rounded-xl px-3 py-2.5 min-w-[240px] bg-white text-slate-800 focus:ring-2 focus:ring-emerald-500/40 focus:border-emerald-500"
                disabled={loadingDrafts}
              >
                <option value="">— None —</option>
                {drafts.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.quoteNumber} — {d.homeownerName ?? '—'} ({d.propertyCity}, {d.propertyState})
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => {
                  if (!selectedDraftId) return;
                  setEditingQuoteId(selectedDraftId);
                  if (typeof window !== 'undefined') {
                    const url = new URL(window.location.href);
                    url.searchParams.set('quoteId', selectedDraftId);
                    window.history.replaceState({}, '', url.pathname + url.search);
                  }
                }}
                disabled={!selectedDraftId || loadingDrafts}
                className="px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-slate-700 font-medium hover:bg-slate-50 hover:border-slate-300 transition-colors disabled:opacity-50"
              >
                Load
              </button>
              {(selectedDraftId || editingQuoteId) && (
                <button
                  type="button"
                  onClick={() => deleteDraft(selectedDraftId || editingQuoteId!)}
                  disabled={loadingDrafts}
                  className="px-4 py-2.5 rounded-xl border border-red-200 bg-red-50 text-red-700 font-medium hover:bg-red-100 transition-colors disabled:opacity-50"
                >
                  Delete draft
                </button>
              )}
            </div>
            {loadingDrafts && <p className="text-sm text-slate-500 mt-1">Loading drafts…</p>}
            {draftLoadError && (
              <div className="mt-2 p-2 rounded-lg bg-red-50 border border-red-200 flex flex-wrap items-center gap-2">
                <p className="text-sm text-red-700 flex-1">{draftLoadError}</p>
                <button
                  type="button"
                  onClick={() => setDraftRetryKey((k) => k + 1)}
                  className="px-3 py-1.5 text-sm font-medium text-red-700 bg-red-100 hover:bg-red-200 rounded-lg"
                >
                  Retry
                </button>
              </div>
            )}
            {!loadingDrafts && !draftLoadError && drafts.length === 0 && (
              <p className="text-sm text-slate-500 mt-1">No drafts yet. Use &quot;Save Draft&quot; to create one.</p>
            )}
          </div>
          <div className="mb-6 p-4 rounded-xl border border-slate-200/80 bg-slate-50/60">
            <label className="block text-sm font-semibold text-slate-700 mb-3">Templates</label>
            <div className="flex flex-wrap items-center gap-3">
              <select
                value={selectedTemplateId ?? ''}
                onChange={(e) => setSelectedTemplateId(e.target.value || null)}
                className="border border-slate-200 rounded-xl px-3 py-2.5 min-w-[200px] bg-white text-slate-800 focus:ring-2 focus:ring-emerald-500/40 focus:border-emerald-500"
                disabled={loadingTemplates}
              >
                <option value="">— None —</option>
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => selectedTemplateId && applyTemplate(selectedTemplateId)}
                disabled={!selectedTemplateId || loadingTemplates}
                className="px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-slate-700 font-medium hover:bg-slate-50 hover:border-slate-300 transition-colors disabled:opacity-50"
              >
                Apply Template
              </button>
              <button
                type="button"
                onClick={saveAsTemplate}
                className="px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-slate-700 font-medium hover:bg-slate-50 hover:border-slate-300 transition-colors"
              >
                Save as Template
              </button>
              {selectedTemplateId && (
                <>
                  <button
                    type="button"
                    onClick={() => updateTemplate(selectedTemplateId)}
                    className="px-4 py-2.5 rounded-xl border border-sky-300 bg-sky-50 text-sky-700 font-medium hover:bg-sky-100 transition-colors"
                  >
                    Update Template
                  </button>
                  <button
                    type="button"
                    onClick={() => deleteTemplate(selectedTemplateId)}
                    className="px-4 py-2.5 rounded-xl border border-red-200 bg-red-50 text-red-700 font-medium hover:bg-red-100 transition-colors"
                  >
                    Delete Template
                  </button>
                </>
              )}
            </div>
            {loadingTemplates && <p className="text-sm text-gray-500 mt-1">Loading templates…</p>}
            {templateLoadError && (
              <div className="mt-2 p-2 rounded-lg bg-red-50 border border-red-200 flex flex-wrap items-center gap-2">
                <p className="text-sm text-red-700 flex-1">{templateLoadError}</p>
                <button
                  type="button"
                  onClick={() => setTemplateRetryKey((k) => k + 1)}
                  className="px-3 py-1.5 text-sm font-medium text-red-700 bg-red-100 hover:bg-red-200 rounded-lg"
                >
                  Retry
                </button>
              </div>
            )}
            {!loadingTemplates && !templateLoadError && templates.length === 0 && (
              <p className="text-sm text-gray-500 mt-1">No templates yet. Use &quot;Save as Template&quot; to create one.</p>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField label="Issue date" error={fieldErrors.issueDate} htmlFor="issueDate">
              <input
                id="issueDate"
                ref={(el) => { refs.current.issueDate = el; }}
                type="date"
                value={issueDate}
                onChange={(e) => setIssueDate(e.target.value)}
                className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                style={inputErrorStyle('issueDate')}
              />
            </FormField>
            <FormField label="Expiration date" error={fieldErrors.expirationDate} htmlFor="expirationDate">
              <input
                id="expirationDate"
                ref={(el) => { refs.current.expirationDate = el; }}
                type="date"
                value={expirationDate}
                onChange={(e) => setExpirationDate(e.target.value)}
                className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                style={inputErrorStyle('expirationDate')}
              />
            </FormField>
          </div>
        </SettingsCard>

        <SettingsCard title={<><span>Homeowner & Property</span> <strong style={{ fontWeight: 700 }}>(**Required**)</strong></>}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField label="Homeowner name" error={fieldErrors.homeownerName} htmlFor="homeownerName">
              <input
                id="homeownerName"
                ref={(el) => { refs.current.homeownerName = el; }}
                type="text"
                value={homeownerName}
                onChange={(e) => setHomeownerName(e.target.value)}
                className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                style={inputErrorStyle('homeownerName')}
              />
            </FormField>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email (optional)</label>
              <input type="email" value={homeownerEmail} onChange={(e) => setHomeownerEmail(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone (optional)</label>
              <input type="text" value={homeownerPhone} onChange={(e) => setHomeownerPhone(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
            </div>
            <div className="md:col-span-2">
              <FormField label="Address line 1" error={fieldErrors.propertyAddress1} htmlFor="propertyAddress1">
                <input
                  id="propertyAddress1"
                  ref={(el) => { refs.current.propertyAddress1 = el; }}
                  type="text"
                  value={propertyAddress1}
                  onChange={(e) => setPropertyAddress1(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  style={inputErrorStyle('propertyAddress1')}
                />
              </FormField>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Address line 2 (optional)</label>
              <input type="text" value={propertyAddress2} onChange={(e) => setPropertyAddress2(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
            </div>
            <FormField label="City" error={fieldErrors.propertyCity} htmlFor="propertyCity">
              <input
                id="propertyCity"
                ref={(el) => { refs.current.propertyCity = el; }}
                type="text"
                value={propertyCity}
                onChange={(e) => setPropertyCity(e.target.value)}
                className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                style={inputErrorStyle('propertyCity')}
              />
            </FormField>
            <FormField label="State" error={fieldErrors.propertyState} htmlFor="propertyState">
              <input
                id="propertyState"
                ref={(el) => { refs.current.propertyState = el; }}
                type="text"
                value={propertyState}
                onChange={(e) => setPropertyState(e.target.value)}
                className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                style={inputErrorStyle('propertyState')}
              />
            </FormField>
            <FormField label="ZIP code" error={fieldErrors.propertyZip} htmlFor="propertyZip">
              <input
                id="propertyZip"
                ref={(el) => { refs.current.propertyZip = el; }}
                type="text"
                value={propertyZip}
                onChange={(e) => setPropertyZip(e.target.value)}
                className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                style={inputErrorStyle('propertyZip')}
              />
            </FormField>
          </div>
        </SettingsCard>

        <SettingsCard title={<><span>Contractor</span> <strong style={{ fontWeight: 700 }}>(**Required**)</strong></>}>
          <p className="text-sm text-gray-600 mb-3">Only contractor name is shown on the homeowner-facing PDF.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField label="Contractor name" error={fieldErrors.contractorBusinessName} htmlFor="contractorBusinessName">
              <input
                id="contractorBusinessName"
                ref={(el) => { refs.current.contractorBusinessName = el; }}
                type="text"
                value={contractorBusinessName}
                onChange={(e) => setContractorBusinessName(e.target.value)}
                className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                style={inputErrorStyle('contractorBusinessName')}
              />
            </FormField>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">License (optional)</label>
              <input type="text" value={contractorLicense} onChange={(e) => setContractorLicense(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Insurance (optional)</label>
              <input type="text" value={contractorInsurance} onChange={(e) => setContractorInsurance(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
            </div>
          </div>
        </SettingsCard>

        <SettingsCard
          title={
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
              <span>Roof Measurements <strong style={{ fontWeight: 700 }}>(**Required**)</strong></span>
              <button
                type="button"
                onClick={() => setRoofReportModalOpen(true)}
                style={{ padding: '6px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, color: '#374151', cursor: 'pointer', background: 'white' }}
              >
                Add Roof Report
              </button>
            </div>
          }
        >
          {measurementsSourceReportNumber && (
            <p className="text-sm text-gray-600 mb-3 font-medium">
              Measurements source: EagleView report {measurementsSourceReportNumber}
            </p>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <FormField label="Roof type" error={fieldErrors.roofType} htmlFor="roofType">
              <input
                id="roofType"
                ref={(el) => { refs.current.roofType = el; }}
                type="text"
                value={roofType}
                onChange={(e) => setRoofType(e.target.value)}
                placeholder="e.g. Asphalt shingle"
                className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                style={inputErrorStyle('roofType')}
              />
            </FormField>
            <FormField label="Roof size (sq ft or squares)" error={fieldErrors.roofSize}>
              <div className="flex gap-2" ref={(el) => { refs.current.roofSize = el; }}>
                <input
                  type="number"
                  min={0}
                  value={roofSizeSqFt}
                  onChange={(e) => setRoofSizeSqFt(e.target.value === '' ? '' : Number(e.target.value))}
                  placeholder="Sq ft"
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  style={inputErrorStyle('roofSize')}
                />
                <input
                  type="number"
                  min={0}
                  value={roofSizeSquares}
                  onChange={(e) => setRoofSizeSquares(e.target.value === '' ? '' : Number(e.target.value))}
                  placeholder="Squares"
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  style={inputErrorStyle('roofSize')}
                />
              </div>
            </FormField>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Pitch (optional)</label>
              <input type="text" value={roofPitch} onChange={(e) => setRoofPitch(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Stories (optional)</label>
              <input
                type="number"
                min={0}
                value={stories}
                onChange={(e) => setStories(e.target.value === '' ? '' : Number(e.target.value))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Waste factor % (optional)</label>
              <input
                type="number"
                min={0}
                step={0.1}
                value={wasteFactorPct}
                onChange={(e) => setWasteFactorPct(e.target.value === '' ? '' : Number(e.target.value))}
                placeholder="e.g. 10"
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Estimated duration (days, optional)</label>
              <input
                type="number"
                min={0}
                value={estimatedDurationDays}
                onChange={(e) => setEstimatedDurationDays(e.target.value === '' ? '' : Number(e.target.value))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              />
            </div>
            {(eagleViewRidgesFt !== '' || eagleViewValleysFt !== '' || eagleViewRakesFt !== '' || eagleViewEavesFt !== '') && (
              <div className="md:col-span-2 lg:col-span-3 p-3 border border-gray-200 rounded-lg bg-gray-50/50">
                <p className="text-sm font-medium text-gray-700 mb-2">EagleView measurements (from report)</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {eagleViewRidgesFt !== '' && (
                    <div>
                      <label className="block text-xs text-gray-500">Ridges (ft)</label>
                      <input type="number" readOnly value={eagleViewRidgesFt} className="w-full border border-gray-200 rounded px-2 py-1 bg-white" />
                    </div>
                  )}
                  {eagleViewValleysFt !== '' && (
                    <div>
                      <label className="block text-xs text-gray-500">Valleys (ft)</label>
                      <input type="number" readOnly value={eagleViewValleysFt} className="w-full border border-gray-200 rounded px-2 py-1 bg-white" />
                    </div>
                  )}
                  {eagleViewRakesFt !== '' && (
                    <div>
                      <label className="block text-xs text-gray-500">Rakes (ft)</label>
                      <input type="number" readOnly value={eagleViewRakesFt} className="w-full border border-gray-200 rounded px-2 py-1 bg-white" />
                    </div>
                  )}
                  {eagleViewEavesFt !== '' && (
                    <div>
                      <label className="block text-xs text-gray-500">Eaves (ft)</label>
                      <input type="number" readOnly value={eagleViewEavesFt} className="w-full border border-gray-200 rounded px-2 py-1 bg-white" />
                    </div>
                  )}
                </div>
              </div>
            )}
            <div className="md:col-span-2 lg:col-span-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">Exclusions (optional)</label>
              <textarea value={exclusions} onChange={(e) => setExclusions(e.target.value)} rows={2} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
            </div>
          </div>
        </SettingsCard>

        <SettingsCard title={<><span>Scope of Work</span> <strong style={{ fontWeight: 700 }}>(**Required**)</strong></>}>
          <FormField label="Scope of work narrative" error={fieldErrors.scopeOfWork} htmlFor="scopeOfWork">
            <textarea
              id="scopeOfWork"
              ref={(el) => { refs.current.scopeOfWork = el; }}
              value={scopeOfWork}
              onChange={(e) => setScopeOfWork(e.target.value)}
              rows={5}
              className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              style={inputErrorStyle('scopeOfWork')}
            />
          </FormField>
        </SettingsCard>

        <SettingsCard title="Roof System Components (optional)">
          <p className="text-sm text-gray-600 mb-3">Informational specs only; pricing is in Itemized Pricing below.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Shingles</label><input type="text" value={systemShingles} onChange={(e) => setSystemShingles(e.target.value)} placeholder="e.g. brand, model, color" className="w-full border border-gray-300 rounded-lg px-3 py-2" /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Underlayment</label><input type="text" value={systemUnderlayment} onChange={(e) => setSystemUnderlayment(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2" /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Starter</label><input type="text" value={systemStarter} onChange={(e) => setSystemStarter(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2" /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Ridge cap</label><input type="text" value={systemRidgeCap} onChange={(e) => setSystemRidgeCap(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2" /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Leak barrier</label><input type="text" value={systemLeakBarrier} onChange={(e) => setSystemLeakBarrier(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2" /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Drip edge</label><input type="text" value={systemDripEdge} onChange={(e) => setSystemDripEdge(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2" /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Ventilation</label><input type="text" value={systemVentilation} onChange={(e) => setSystemVentilation(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2" /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Pipe boots</label><input type="text" value={systemPipeBoots} onChange={(e) => setSystemPipeBoots(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2" /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Decking allowance</label><input type="text" value={systemDeckingAllowance} onChange={(e) => setSystemDeckingAllowance(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2" /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Warranties (spec)</label><input type="text" value={systemWarranties} onChange={(e) => setSystemWarranties(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2" /></div>
          </div>
        </SettingsCard>

        <SettingsCard title="Allowances & Unit Rates (Optional)">
          <p className="text-sm text-gray-600 mb-3">Values in cents (e.g. 1500 = $15.00).</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Extra layers ($/100 sq ft, cents)</label><input type="number" min={0} value={allowanceExtraLayersCents} onChange={(e) => setAllowanceExtraLayersCents(e.target.value === '' ? '' : Number(e.target.value))} className="w-full border border-gray-300 rounded-lg px-3 py-2" /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Decking ($/sheet, cents)</label><input type="number" min={0} value={allowanceDeckingCents} onChange={(e) => setAllowanceDeckingCents(e.target.value === '' ? '' : Number(e.target.value))} className="w-full border border-gray-300 rounded-lg px-3 py-2" /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Ridge vent ($/lf, cents)</label><input type="number" min={0} value={allowanceRidgeVentCents} onChange={(e) => setAllowanceRidgeVentCents(e.target.value === '' ? '' : Number(e.target.value))} className="w-full border border-gray-300 rounded-lg px-3 py-2" /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Chimney cricket ($/chimney, cents)</label><input type="number" min={0} value={allowanceChimneyCricketCents} onChange={(e) => setAllowanceChimneyCricketCents(e.target.value === '' ? '' : Number(e.target.value))} className="w-full border border-gray-300 rounded-lg px-3 py-2" /></div>
          </div>
        </SettingsCard>

        <SettingsCard
          title={<><span>Itemized Pricing</span> <strong style={{ fontWeight: 700 }}>(**Required**)</strong></>}
          className={fieldErrors.lineItems ? 'ring-1 ring-red-300 border-red-300' : ''}
          style={fieldErrors.lineItems ? { borderColor: '#fca5a5', boxShadow: '0 0 0 1px #fca5a5', backgroundColor: '#fef2f2' } : undefined}
        >
          <div ref={(el) => { refs.current.lineItems = el; }}>
            <p className="text-sm text-slate-600 mb-3">Each line item: description required. At least one must have a price (Total $ &gt; 0). Use – or leave Total empty for no price.</p>
            <div className="overflow-x-auto -mx-1 px-1">
              <div className="inline-block min-w-[640px] w-full">
                {/* Header row */}
                <div className="grid grid-cols-[100px_minmax(0,1fr)_4rem_4rem_5.5rem_5.5rem_4.5rem] gap-2 items-center mb-2 py-2 px-2 rounded-t-xl bg-slate-100/80 text-sm font-medium text-slate-600 border border-slate-200/80 border-b-0">
                  <span>Category</span>
                  <span>Description</span>
                  <span>Qty</span>
                  <span>Unit</span>
                  <span>Unit $</span>
                  <span>Total $</span>
                  <span aria-hidden className="invisible">Remove</span>
                </div>
                {lineItems.map((row, idx) => {
                  const rowInvalidDesc = fieldErrors.lineItems && row.description.trim() === '';
                  const descInputErrorStyle: React.CSSProperties = rowInvalidDesc ? { borderColor: '#ef4444', boxShadow: '0 0 0 1px #fca5a5', backgroundColor: '#fef2f2' } : {};
                  const isLast = idx === lineItems.length - 1;
                  return (
                    <div
                      key={row.id}
                      className={`grid grid-cols-[100px_minmax(0,1fr)_4rem_4rem_5.5rem_5.5rem_4.5rem] gap-2 items-center p-2.5 border border-slate-200/80 border-t-0 ${isLast ? 'rounded-b-xl' : ''} ${rowInvalidDesc ? 'bg-red-50/60' : idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}`}
                      style={rowInvalidDesc ? { borderColor: '#fca5a5' } : undefined}
                    >
                      <select
                        value={row.category}
                        onChange={(e) => updateLineItem(row.id, 'category', e.target.value as LineItemRow['category'])}
                        className="border border-slate-200 rounded-lg px-2 py-1.5 text-sm w-full min-w-0 bg-white focus:ring-2 focus:ring-emerald-500/40 focus:border-emerald-500"
                      >
                        {LINE_ITEM_CATEGORIES.map((c) => (
                          <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>
                        ))}
                      </select>
                      <input
                        type="text"
                        placeholder="Description *"
                        value={row.description}
                        onChange={(e) => updateLineItem(row.id, 'description', e.target.value)}
                        className="border rounded-lg px-2 py-1.5 text-sm min-w-0 w-full bg-white focus:ring-2 focus:ring-emerald-500/40 focus:border-emerald-500"
                        style={descInputErrorStyle}
                      />
                      <input
                        type="text"
                        placeholder="Qty"
                        value={row.quantity === '' ? '' : row.quantity}
                        onChange={(e) => {
                          const v = e.target.value.trim();
                          updateLineItem(row.id, 'quantity', v === '' ? '' : Number(v));
                        }}
                        className="border border-slate-200 rounded-lg px-2 py-1.5 text-sm w-full min-w-0 bg-white focus:ring-2 focus:ring-emerald-500/40 focus:border-emerald-500"
                      />
                      <input
                        type="text"
                        placeholder="Unit"
                        value={row.unit}
                        onChange={(e) => updateLineItem(row.id, 'unit', e.target.value)}
                        className="border border-slate-200 rounded-lg px-2 py-1.5 text-sm w-full min-w-0 bg-white focus:ring-2 focus:ring-emerald-500/40 focus:border-emerald-500"
                      />
                      <input
                        type="text"
                        placeholder="Unit $"
                        value={row.unitPriceCents === '' ? '' : (Number(row.unitPriceCents) / 100).toFixed(2)}
                        onChange={(e) => {
                          const raw = e.target.value.trim();
                          if (raw === '' || raw === '–' || raw === '-') {
                            updateLineItem(row.id, 'unitPriceCents', '');
                            return;
                          }
                          const num = parseFloat(raw.replace(/,/g, ''));
                          if (!Number.isNaN(num) && num >= 0) updateLineItem(row.id, 'unitPriceCents', Math.round(num * 100));
                        }}
                        className="border border-slate-200 rounded-lg px-2 py-1.5 text-sm w-full min-w-0 bg-white focus:ring-2 focus:ring-emerald-500/40 focus:border-emerald-500"
                      />
                      <input
                        type="text"
                        placeholder="Total $ or –"
                        value={row.totalCents === '' ? '' : (Number(row.totalCents) / 100).toString()}
                        onChange={(e) => {
                          const raw = e.target.value.trim();
                          if (raw === '' || raw === '–' || raw === '-') {
                            updateLineItem(row.id, 'totalCents', '');
                            return;
                          }
                          const num = parseFloat(raw.replace(/,/g, ''));
                          if (!Number.isNaN(num) && num >= 0) updateLineItem(row.id, 'totalCents', Math.round(num * 100));
                        }}
                        className="border border-slate-200 rounded-lg px-2 py-1.5 text-sm w-full min-w-0 bg-white focus:ring-2 focus:ring-emerald-500/40 focus:border-emerald-500"
                      />
                      <button type="button" onClick={() => removeLineItem(row.id)} className="text-red-600 hover:text-red-700 hover:underline text-sm whitespace-nowrap py-1 transition-colors">Remove</button>
                    </div>
                  );
                })}
              </div>
            </div>
            {fieldErrors.lineItems && <p className="mt-2 text-sm text-red-600" role="alert">{fieldErrors.lineItems}</p>}
            <button type="button" onClick={addLineItem} className="mt-3 px-4 py-2.5 rounded-xl border border-dashed border-slate-300 bg-slate-50/60 text-slate-700 text-sm font-medium hover:bg-slate-100 hover:border-slate-400 transition-colors">
              + Add Line Item
            </button>
            <div className="mt-6 pt-4 flex flex-wrap gap-6 items-center border-t border-slate-200/80">
              <span className="text-sm text-slate-600">Subtotal: ${(subtotalCents / 100).toFixed(2)}</span>
              <span className="text-sm text-slate-600">Tax and fees: ${(taxAndFeesCents / 100).toFixed(2)}</span>
              <span className="text-base font-bold text-slate-800">Total: ${(totalCents / 100).toFixed(2)}</span>
            </div>
          </div>
        </SettingsCard>

        <SettingsCard title="Warranties (Optional)">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <FormField label="Payment schedule">
              <textarea value={paymentSchedule} onChange={(e) => setPaymentSchedule(e.target.value)} rows={2} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-y" />
            </FormField>
            <FormField label="Manufacturer warranty">
              <input type="text" value={manufacturerWarranty} onChange={(e) => setManufacturerWarranty(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
            </FormField>
            <FormField label="Workmanship warranty">
              <input type="text" value={workmanshipWarranty} onChange={(e) => setWorkmanshipWarranty(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
            </FormField>
          </div>
        </SettingsCard>

        <SettingsCard title="Terms & Exclusions (Optional)">
          <div className="grid grid-cols-1 gap-4">
            <FormField label="Terms & conditions">
              <textarea value={termsAndConditions} onChange={(e) => setTermsAndConditions(e.target.value)} rows={3} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-y" />
            </FormField>
            <FormField label="Exclusions">
              <textarea value={exclusions} onChange={(e) => setExclusions(e.target.value)} rows={2} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-y" />
            </FormField>
          </div>
        </SettingsCard>

        <SettingsCard title="Other (Optional)">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <FormField label="Created from project ID">
              <input type="text" value={createdFromProjectId} onChange={(e) => setCreatedFromProjectId(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
            </FormField>
            <FormField label="Materials summary">
              <textarea value={materialsSummary} onChange={(e) => setMaterialsSummary(e.target.value)} rows={2} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-y" />
            </FormField>
            <FormField label="Ventilation summary">
              <textarea value={ventilationSummary} onChange={(e) => setVentilationSummary(e.target.value)} rows={2} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-y" />
            </FormField>
          </div>
        </SettingsCard>

        <div className="pt-6 mt-2 border-t border-slate-200/80 flex flex-wrap gap-3">
          {onNavigate && (
            <button type="button" onClick={() => onNavigate('admin-dashboard')} className="px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-slate-700 text-sm font-medium hover:bg-slate-50 hover:border-slate-300 transition-colors shadow-sm">
              Back to Dashboard
            </button>
          )}
        </div>
      </form>

      {/* Add Roof Report modal */}
      {roofReportModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="roof-report-title"
        >
          <div className="bg-white rounded-xl shadow-lg max-w-lg w-full mx-4 max-h-[85vh] flex flex-col">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h2 id="roof-report-title" className="text-lg font-semibold text-gray-900">
                Add Roof Report (EagleView)
              </h2>
              <button
                type="button"
                onClick={() => {
                  setRoofReportModalOpen(false);
                  setRoofReportExtracted(null);
                  setRoofReportIdForApply(null);
                }}
                className="text-gray-500 hover:text-gray-700 p-1 rounded"
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="p-4 overflow-y-auto flex-1">
              <input
                ref={roofReportFileRef}
                type="file"
                accept=".pdf,application/pdf"
                onChange={handleRoofReportUpload}
                className="hidden"
              />
              <button
                type="button"
                disabled={uploadingRoofReport || parsingRoofReport}
                onClick={() => roofReportFileRef.current?.click()}
                className="w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-gray-400 font-medium text-gray-700 disabled:opacity-50"
              >
                {uploadingRoofReport || parsingRoofReport ? 'Uploading / parsing…' : 'Choose PDF to upload'}
              </button>
              {roofReportExtracted && (
                <div className="mt-4 p-3 border border-gray-200 rounded-lg bg-gray-50">
                  <p className="text-sm font-medium text-gray-800 mb-2">Extracted (preview)</p>
                  <ul className="text-sm text-gray-700 space-y-1">
                    {roofReportExtracted.roofAreaSqFt != null && (
                      <li>Roof area: {String(roofReportExtracted.roofAreaSqFt)} sq ft</li>
                    )}
                    {roofReportExtracted.predominantPitch != null && (
                      <li>Predominant pitch: {String(roofReportExtracted.predominantPitch)}</li>
                    )}
                    {roofReportExtracted.stories != null && (
                      <li>Stories: {String(roofReportExtracted.stories)}</li>
                    )}
                    {roofReportExtracted.ridgesFt != null && (
                      <li>Ridges: {String(roofReportExtracted.ridgesFt)} ft</li>
                    )}
                    {roofReportExtracted.valleysFt != null && (
                      <li>Valleys: {String(roofReportExtracted.valleysFt)} ft</li>
                    )}
                    {roofReportExtracted.rakesFt != null && (
                      <li>Rakes: {String(roofReportExtracted.rakesFt)} ft</li>
                    )}
                    {roofReportExtracted.eavesFt != null && (
                      <li>Eaves: {String(roofReportExtracted.eavesFt)} ft</li>
                    )}
                  </ul>
                  <button
                    type="button"
                    onClick={handleApplyRoofReportToQuote}
                    className="mt-3 px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg"
                  >
                    Apply to Quote
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      </div>
    </ResponsiveContainer>
  );
};

export default AdminCreateQuotePage;
// CB:END admin-create-quote-page
